import { ref } from 'vue'
import { ROUTES } from '@/constants'
import { SECTIONS } from '@/types/sections'
import helpers from '@/utils/helpers.ts'
import cloneDeep from 'lodash/cloneDeep'
import { defineStore } from 'pinia'
import { clone, MIME_TYPES } from '@deepcloud/deep-ui-utils'
import { useParams } from '@deepcloud/deep-ui-utils/router'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core.ts'
import { useFilesTree } from '@/composables/files-tree'
import { useNodeApi } from '@/composables/node/node-api'
import {
  useTaskUploads,
  useTaskUploadUploader,
} from '@/components/upload/composables'
import type {
  TaskUploadItemFile,
  UploadEntriesOutput,
} from '@/components/upload'
import type {
  TaskUploadCallback,
  TaskUploadNode,
  TaskUploadSection,
  TaskUploadTargetNode,
} from '@/components/upload/types.ts'
import type { Sections } from '@/types/sections'
import type { Node, PathSegment } from '@deepcloud/api-deep-box'
import type { RouteLocation } from 'vue-router'
import type { TaskUploadDeepBox, TaskUploadDeepBoxNodes } from './types'

function isSectionCurrentSection(
  targetSection: string | undefined | null,
  routeSection: string | undefined | null,
) {
  return !!routeSection && (routeSection === targetSection || !targetSection)
}

function uploadRouteMatchCurrentRoute(
  uploadRoute: RouteLocation,
  currentRoute: RouteLocation,
) {
  if (!uploadRoute || !currentRoute) return false
  const isSameRouteName = uploadRoute.name === currentRoute.name
  const isSameType = uploadRoute.params.type === currentRoute.params.type
  const isSameBox = uploadRoute.params.box === currentRoute.params.box

  return isSameRouteName && isSameType && isSameBox
}

export const useDeepBoxUploadStore = defineStore('deepBoxUpload', () => {
  const taskUpload = ref<TaskUploadDeepBox[]>([])

  const route = useRoute()
  const router = useRouter()

  const params = useParams<{
    box: string
    type: string
  }>()

  const nodeApi = useNodeApi()
  const { refreshFilesTreeOnMove } = useFilesTree()

  const { getTaskUploadById } = useTaskUploads(taskUpload)

  const sharedComposable = createSharedComposable(useTaskUploadUploader)
  const { setNodesToUpload } = sharedComposable(taskUpload)

  function shouldRefreshState(
    targetNode: TaskUploadTargetNode,
    parentNodeId: string,
    rootTargetNodeId: string,
    routeTo: RouteLocation,
  ) {
    /**
     *  Case 1:
     *  - Folders + Files are uploaded to the root ( documents -> section: 'files' )
     *  Case 2:
     *  - Folders + Files are uploaded to some nested folders ( documents -> section: 'fileNodes' )
     *  Case 3:
     *  - Only Folders  are uploaded to the root ( documents -> section: 'files' )
     *  Case 4:
     *  - Only Files are uploaded to some nested folders ( documents -> section: 'fileNodes' )
     *  Case 5:
     *  - Files are uploaded to root inbox ( inbox -> section: 'queue' or 'inbox' )
     *
     */

    const currentRouteSection = helpers.getSectionFromRoute(route)
    const routeToSection = helpers.getSectionFromRoute(routeTo)
    const targetSection = targetNode
      ? helpers.getUploadSectionFromMimeType(targetNode?.mimeType)
      : undefined

    let _isSectionCurrentSection
    if (targetNode) {
      _isSectionCurrentSection = isSectionCurrentSection(
        targetSection,
        currentRouteSection,
      )
    } else {
      _isSectionCurrentSection = isSectionCurrentSection(
        routeToSection,
        currentRouteSection,
      )
    }

    let refreshState
    if (route?.params?.node) {
      refreshState = parentNodeId === route.params.node
    } else {
      refreshState =
        _isSectionCurrentSection &&
        rootTargetNodeId === parentNodeId &&
        uploadRouteMatchCurrentRoute(routeTo, route)
    }
    return refreshState
  }

  const onSuccessFolderUpload: TaskUploadCallback = async (
    taskUploadId,
    folderId,
  ) => {
    const taskUpload = getTaskUploadById(taskUploadId) as TaskUploadDeepBox
    if (!taskUpload || !taskUpload.folders) return
    const folderNode = taskUpload.folders[folderId]

    if (!folderNode.data?.node) return

    // if the new folder is within the rootTargetNode, update the 'deepbox' state, so that we can see it on the frontend
    const route = taskUpload.metadata.route
    const targetNode = taskUpload.target.node
    let parentNodeId: string | null = null
    const deepBoxCoreStore = useDeepBoxCoreStore()

    // get the rootTargetNodeId. It can be either the files root node id or the sub-folder node id we're in
    let rootTargetNodeId: string | null
    if (targetNode) {
      rootTargetNodeId = targetNode.nodeId
    } else {
      rootTargetNodeId =
        (route.params.node as string) ??
        deepBoxCoreStore.selectedBox?.roots?.files ??
        null
    }

    // get the right parentNodeId:
    // If the file has no parent name set, get the rootTargetNodeId
    parentNodeId = rootTargetNodeId
    // Otherwise, get the parentNodeId from the folders map
    if (
      folderNode.data?.parent &&
      taskUpload.folders &&
      taskUpload.folders[folderNode.data.parent]
    ) {
      parentNodeId =
        taskUpload.folders[folderNode.data.parent].data?.nodeId ?? parentNodeId
    }

    // add new folder to nodes store
    const routeSection = helpers.getSectionFromRoute(route)

    const refreshState = shouldRefreshState(
      targetNode,
      parentNodeId,
      rootTargetNodeId,
      route,
    )

    const routeClone = cloneDeep(route)

    if (refreshState && !!routeSection) {
      nodeApi.addFileFulfilled({
        node: folderNode.data.node,
        section: routeSection,
        route: {
          name: routeClone.name as string,
          params: routeClone.params,
          query: routeClone.query,
        },
      })
    }
    // add new created folder to tree
    await refreshFilesTreeOnMove(parentNodeId)
  }

  function getTaskUploadItemFileParentId(
    taskUpload: TaskUploadDeepBox,
    taskUploadFile: TaskUploadNode,
  ) {
    const {
      files,
      folders,
      metadata: { route },
      target: { node: targetNode },
    } = taskUpload
    const deepBoxCoreStore = useDeepBoxCoreStore()

    // Determine if we are targeting the inbox
    const isInboxTargetNode = targetNode
      ? targetNode.mimeType === MIME_TYPES.MIME_TYPE_INBOX
      : route?.name === ROUTES.ROUTE_QUEUE

    // Determine rootTargetNodeId based on the target
    const rootTargetNodeId = isInboxTargetNode
      ? deepBoxCoreStore.selectedBox?.roots?.queue
      : (targetNode?.nodeId ??
        route?.params.node ??
        deepBoxCoreStore.selectedBox?.roots?.files)

    console.log('uploadFiles - folders', folders)
    console.log('uploadFiles - files', files)
    console.log('uploadFiles - route', route)
    console.log('uploadFiles - rootTargetNodeId', rootTargetNodeId)

    // Determine the parentNodeId depending on context
    if (isInboxTargetNode) {
      return rootTargetNodeId
    }

    if (
      folders &&
      taskUploadFile.data?.idFolder &&
      folders[taskUploadFile.data.idFolder]?.data?.nodeId
    ) {
      return folders[taskUploadFile.data.idFolder]?.data?.nodeId
    }

    return rootTargetNodeId
  }

  const onSuccessFileUpload: TaskUploadCallback = async (
    taskUploadId,
    fileIndex,
  ) => {
    const taskUpload = getTaskUploadById(taskUploadId) as TaskUploadDeepBox
    if (!taskUpload || !taskUpload.files) return
    const fileNode = taskUpload.files[fileIndex]

    if (!fileNode.data?.node) return

    // prepare upload params (derive parent node id)
    const route = taskUpload.metadata.route
    const targetNode = taskUpload.target.node
    const parentNodeId = getTaskUploadItemFileParentId(taskUpload, fileNode)
    const routeSection = helpers.getSectionFromRoute(route)

    const targetSection = targetNode
      ? helpers.getUploadSectionFromMimeType(targetNode?.mimeType)
      : undefined
    let section
    let rootTargetNodeId
    if (targetNode) {
      section = targetSection
      rootTargetNodeId = targetNode.nodeId
    } else {
      section = routeSection
      rootTargetNodeId = route?.params?.node
    }

    console.log('uploadFile - payload', {
      targetNode,
      route,
      parentNodeId,
      taskUploadId,
    })

    if (!parentNodeId) return

    // update nodes references
    const refreshState = shouldRefreshState(
      targetNode,
      parentNodeId,
      rootTargetNodeId,
      route,
    )

    // the state should only be refreshed if just uploaded node should be added to the current nodes showing
    if (refreshState) {
      // append the uploaded file to the current list of nodes, so that we can see it appearing
      nodeApi.addFileFulfilled({
        section,
        node: fileNode.data.node,
        route: {
          name: route.name as string,
          params: route.params,
          query: route.query,
        },
      })
    } else {
      // update inbox counter if add node to inbox
      if (
        targetSection &&
        ([SECTIONS.QUEUE, SECTIONS.INBOX] as Sections[]).includes(targetSection)
      ) {
        const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
        deepBoxBoxInfoStore.increaseBoxInfoQueueCounter()
      }
    }
  }

  async function uploadEntries(
    entries: UploadEntriesOutput[],
    targetNode: Node | PathSegment,
  ) {
    console.log('uploadEntries - targetNode', targetNode)
    let targetRoute
    let isInbox
    if (targetNode) {
      isInbox = targetNode.mimeType === MIME_TYPES.MIME_TYPE_INBOX
      const targetSection = helpers.getUploadSectionFromMimeType(
        targetNode?.mimeType,
      )

      const isNestedRoute = targetSection === 'fileNodes'

      let p: { type: string; box: string; node?: string } = {
        type: params.value.type,
        box: params.value.box,
      }

      if (isNestedRoute) {
        p = {
          ...p,
          node: targetNode.nodeId,
        }
      }
      const resolvedRoute = router.resolve({
        name: helpers.getRouteNameBySectionName(targetSection),
        params: p,
      })
      if (resolvedRoute) {
        targetRoute = {
          name: resolvedRoute.name,
          params: clone(resolvedRoute.params),
          query: clone(resolvedRoute.query),
        }
      }
    } else {
      isInbox = route.name === ROUTES.ROUTE_QUEUE
      targetRoute = {
        name: route.name,
        params: clone(route.params),
        query: clone(route.query),
      }
    }

    let section: TaskUploadSection

    const allowedSections: TaskUploadSection[] = ['inbox', 'files']
    let sectionRoute = helpers.getSectionFromRoute(targetRoute)

    if (targetNode) {
      const sectionTargetNode = helpers.getUploadSectionFromMimeType(
        targetNode.mimeType,
      )
      if (sectionTargetNode) {
        sectionRoute = sectionTargetNode
      }
    }

    if (!sectionRoute) return

    if (allowedSections.includes(sectionRoute as TaskUploadSection)) {
      section = sectionRoute as TaskUploadSection
    } else if (sectionRoute === 'queue') {
      section = 'inbox'
    } else if (sectionRoute === 'fileNodes') {
      section = 'files'
    } else {
      return
    }

    if (!allowedSections.includes(section)) return

    entries.forEach((entry) => {
      let payload: TaskUploadDeepBoxNodes = {
        metadata: {
          route: cloneDeep(targetRoute),
        },
        target: {
          node: targetNode as Node,
          typeId: route.params.type as string,
          boxId: route.params.box as string,
          section,
        },
        onSuccessFile: onSuccessFileUpload,
        onSuccessFolder: onSuccessFolderUpload,
      }

      if (entry.hasFolders && !isInbox) {
        payload = {
          ...payload,
          folders: entry.folders,
        }
      }

      if (entry.files.length > 0) {
        payload = {
          ...payload,
          files: entry.files as TaskUploadItemFile[],
        }
      }

      setNodesToUpload(payload)
    })
  }
  return {
    taskUpload,
    uploadEntries,
  }
})
